import { createSlice } from "@reduxjs/toolkit";
import { captureException } from "@sentry/react";
import i18n from "i18next";
import { Permission, type VideoVariant } from "store/api/generatedApi";
import { AppThunk } from "store/store";
import {
  dumpLanguage,
  loadLanguage,
  dumpHotkeysEnabled,
  loadHotkeysEnabled,
  dumpManualReviewAreaHotkeyBehaviour,
  loadManualReviewAreaHotkeyBehaviour,
  dumpLowMemoryVideoMode,
  loadLowMemoryVideoMode,
  dumpVideoQuality,
  loadVideoQuality,
} from "../persistence";
import type { AltAreaBehavior } from "components/scrapEyeLabelling/hooks/useLabellingKeyboard";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/en-gb";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/pl";

export type TenantPermissions = Record<string, Permission[]>;

export interface SettingsState {
  locale: string;
  displayName: string;
  userUid: string | null;
  email: string | null;
  tenantPermissions: TenantPermissions;
  isAdmin: boolean;
  isAdminPaused: boolean;
  hotkeysEnabled: boolean;
  manualReviewAreaHotkeyBehaviour: AltAreaBehavior;
  lowMemoryVideoMode: boolean;
  videoQuality: VideoVariant;
}

export interface AuthUpdate {
  tenantPermissions: TenantPermissions;
  isAdmin: boolean;
  isAdminPaused: boolean;
  userUid: string;
  displayName: string;
  email: string;
}

export const settingsSlice = createSlice({
  name: "settings",
  initialState: {
    locale: loadLanguage() ?? navigator.language,
    tenantPermissions: {},
    displayName: "",
    hotkeysEnabled: loadHotkeysEnabled(),
    manualReviewAreaHotkeyBehaviour: loadManualReviewAreaHotkeyBehaviour(),
    lowMemoryVideoMode: loadLowMemoryVideoMode(),
    videoQuality: loadVideoQuality(),
  } as SettingsState,
  reducers: {
    languageSelected: (state, action: { payload: string }) => {
      state.locale = action.payload;
    },
    hotkeysEnabledChanged: (state, action: { payload: boolean }) => {
      state.hotkeysEnabled = action.payload;
    },
    manualReviewAreaHotkeyBehaviourChanged: (
      state,
      action: { payload: AltAreaBehavior }
    ) => {
      state.manualReviewAreaHotkeyBehaviour = action.payload;
    },
    lowMemoryVideoModeChanged: (state, action: { payload: boolean }) => {
      state.lowMemoryVideoMode = action.payload;
    },
    videoQualityChanged: (state, action: { payload: VideoVariant }) => {
      state.videoQuality = action.payload;
    },
    authUpdate: (state, action: { payload: AuthUpdate }) => {
      state.tenantPermissions = action.payload.tenantPermissions;
      state.isAdmin = action.payload.isAdmin;
      state.isAdminPaused = action.payload.isAdminPaused;
      state.userUid = action.payload.userUid;
      state.displayName = action.payload.displayName;
      state.email = action.payload.email;
    },
  },
});

export const {
  languageSelected,
  hotkeysEnabledChanged,
  manualReviewAreaHotkeyBehaviourChanged,
  lowMemoryVideoModeChanged,
  videoQualityChanged,
  authUpdate,
} = settingsSlice.actions;

export const changeLanguage =
  (language: string): AppThunk =>
  async (dispatch) => {
    dumpLanguage(language);
    try {
      await i18n.changeLanguage(language);
      dayjs.locale(language);
      dispatch(languageSelected(language));
    } catch (error) {
      captureException(error);
    }
  };

export const changeHotkeysEnabled =
  (hotkeysEnabled: boolean): AppThunk =>
  (dispatch) => {
    dumpHotkeysEnabled(hotkeysEnabled);
    dispatch(hotkeysEnabledChanged(hotkeysEnabled));
  };

export const changeManualReviewAreaHotkeyBehaviour =
  (behaviour: AltAreaBehavior): AppThunk =>
  (dispatch) => {
    dumpManualReviewAreaHotkeyBehaviour(behaviour);
    dispatch(manualReviewAreaHotkeyBehaviourChanged(behaviour));
  };

export const changeLowMemoryVideoMode =
  (enabled: boolean): AppThunk =>
  (dispatch) => {
    dumpLowMemoryVideoMode(enabled);
    dispatch(lowMemoryVideoModeChanged(enabled));
  };

export const changeVideoQuality =
  (quality: VideoVariant): AppThunk =>
  (dispatch) => {
    dumpVideoQuality(quality);
    dispatch(videoQualityChanged(quality));
  };
